import React, { useEffect } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Navbar, Footer } from '../components/Layout';
import { LEGAL_DOCS, NON_BINDING_NOTICE, LEGAL_META } from '../data/legalTexts';

const SLUG_TO_KEY = {
  'aviso-legal': 'aviso_legal',
  'politica-de-privacidad': 'privacidad',
  'politica-de-cookies': 'cookies',
  'terminos-y-condiciones': 'terminos',
};

const LegalPage = () => {
  const location = useLocation();
  const slug = location.pathname.replace(/^\//, '').replace(/\/$/, '');
  const { i18n } = useTranslation();
  const lang = (i18n.language || 'es').split('-')[0];
  const docKey = SLUG_TO_KEY[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!docKey) return <Navigate to="/" replace />;

  const docs = LEGAL_DOCS[lang] || LEGAL_DOCS.es;
  const doc = docs[docKey];
  const notice = NON_BINDING_NOTICE[lang];

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col" data-testid={`legal-${slug}`}>
      <Navbar />
      <main className="flex-1 max-w-4xl mx-auto px-4 pt-24 pb-16 w-full">
        <article className="bg-white rounded-2xl shadow-sm border p-6 md:p-10">
          <header className="mb-8 pb-6 border-b">
            <h1 className="text-3xl md:text-4xl font-bold text-slate-800 tracking-tight" style={{ fontFamily: 'Georgia, serif' }}>
              {doc.title}
            </h1>
            <p className="text-sm text-slate-500 mt-2">{doc.lastUpdated}</p>
            <div className="mt-3 text-xs text-slate-500 leading-relaxed">
              {LEGAL_META.name} · NIF {LEGAL_META.nif} · {LEGAL_META.address}
              <br />
              Tel. {LEGAL_META.phone} · {LEGAL_META.email}
            </div>
            {notice && (
              <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                {notice}
              </div>
            )}
          </header>

          <div className="prose prose-slate max-w-none">
            {doc.sections.map((sec, idx) => (
              <section key={idx} className="mb-6">
                <h2 className="text-lg md:text-xl font-semibold text-slate-800 mt-6 mb-3">
                  {sec.heading}
                </h2>
                {sec.paragraphs?.map((p, i) => (
                  <p key={i} className="text-slate-700 leading-relaxed mb-3 text-[15px]">
                    {p}
                  </p>
                ))}
                {sec.list && (
                  <ul className="list-disc pl-6 space-y-1.5 mb-3 text-slate-700 text-[15px]">
                    {sec.list.map((item, i) => (
                      <li key={i} className="leading-relaxed">{item}</li>
                    ))}
                  </ul>
                )}
                {sec.afterList?.map((p, i) => (
                  <p key={`al-${i}`} className="text-slate-700 leading-relaxed mb-3 text-[15px]">
                    {p}
                  </p>
                ))}
                {sec.table && (
                  <div className="overflow-x-auto my-4 -mx-2 md:mx-0">
                    <table className="min-w-full text-[13px] border border-slate-200 rounded-lg overflow-hidden">
                      <thead className="bg-slate-100">
                        <tr>
                          {sec.table.headers.map((h, i) => (
                            <th key={i} className="px-3 py-2 text-left font-semibold text-slate-700 border-b border-slate-200">
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sec.table.rows.map((row, ri) => (
                          <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'}>
                            {row.map((cell, ci) => (
                              <td key={ci} className="px-3 py-2 text-slate-600 border-b border-slate-100 align-top">
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            ))}
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default LegalPage;
